import {
  type Cover,
  PhotoBookOrderValidation,
} from 'components/PhotoBook/PhotoBookProvider/PhotoBook.types'
import { MIN_PHOTO_BOOK_PAGES } from '@mixtiles/web-backend-shared'
import { DEFAULT_BOOK_SIZE } from 'services/PhotoBook/PhotoBook.consts'
import { observable, action, computed, makeObservable } from 'mobx'
import { logger } from 'services/logger'
import type { StoredPhotoBook } from 'services/PhotoBook/photoBookSaver.types'
import type { PhotoBookStorageBackend } from 'services/PhotoBook/photoBookStorageBackend.types'
import { photosInPages } from 'services/PhotoBook/photoBookUtils'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { isClient } from 'utils/runtimeUtils'
import { PhotoBookStoragePersister } from './PhotoBookStoragePersister'
import {
  PhotoBookDraftSyncer,
  type PhotoBookDraftSyncDeps,
} from './PhotoBookDraftSyncer'

export type PhotoBookUpdater = (book: StoredPhotoBook) => void

export class PhotoBookDataStore {
  // The storage slot this store owns, and the identity of whatever book sits
  // in it: the parent hands a store only books whose id matches its slot.
  readonly bookId: string

  private readonly storagePersister: PhotoBookStoragePersister

  // Null when the parent gave no draft plumbing: a store built without it keeps
  // its book local.
  private readonly draftSyncer: PhotoBookDraftSyncer | null

  // The whole book — the one the editor renders, the cart prices and storage
  // keeps. Deep observable: domain edits mutate it in place through updateBook,
  // so readers outside reactions must snapshot it first.
  @observable accessor bookData: StoredPhotoBook | null = null

  // Shared with the checkout sheet, which validates the order from outside the
  // editor's subtree, and with the editor's limitations popup, which is
  // dismissible — so this cannot be derived from the photo count alone. It
  // belongs to the book that was on screen when it was raised: the popup fires
  // off the status alone, so every whole-book swap clears it rather than
  // warning about a book that was never validated.
  @observable accessor orderValidationStatus: PhotoBookOrderValidation =
    PhotoBookOrderValidation.VALID

  // On while the editor rebuilds this book's pages against the photo bank, so
  // the rebuild can't persist an emptied book over the stored one. Off by
  // default: a stored book whose editor never mounts still reaches storage and
  // the remote channels on any page load.
  @observable accessor isEditorInitializing = false

  // On while an editor route shows this book. The parent's merge drops a
  // same-id arrival for a book on screen — local wins while the user is in it.
  @observable accessor isEditorMounted = false

  // On once the editor page has painted past its splash overlay — an empty
  // editor with no book to load counts as ready. Anything the user is meant to
  // read on top of the editor waits for this, not for isEditorMounted, which
  // flips as soon as the route resolves a store.
  @observable accessor isEditorReady = false

  constructor(
    bookId: string,
    storageBackendPromise: Promise<PhotoBookStorageBackend>,
    storedBook: StoredPhotoBook | null = null,
    draftSyncDeps: PhotoBookDraftSyncDeps | null = null
  ) {
    this.bookId = bookId
    this.storagePersister = new PhotoBookStoragePersister(
      this.bookId,
      () => this.bookToPersist,
      storageBackendPromise
    )
    this.draftSyncer = draftSyncDeps
      ? new PhotoBookDraftSyncer(
          this.bookId,
          () => this.bookToPersist,
          draftSyncDeps
        )
      : null

    if (isClient()) {
      // The stored book arrives from the parent's one storage load, before
      // the autoruns start, so a seeded store never persists its own seed back.
      this.bookData = storedBook
      if (storedBook) {
        this.storagePersister.markBookLoaded(storedBook)
      }
      this.storagePersister.startPersisting()
      this.draftSyncer?.startSyncing()
    }
    makeObservable(this)
  }

  hasBook(): boolean {
    return readOutsideReaction(() => this.bookData !== null)
  }

  @computed
  get photosCount(): number {
    return photosInPages(this.bookData?.pages ?? []).length
  }

  // A book starts at its cover: the editor picks one the moment the first photos
  // land, and pages follow. Whatever book was here is replaced. Resolves with
  // whether the seed write landed, so the caller can roll a book out of a full
  // quota.
  @action
  createBook(cover: Cover): Promise<boolean> {
    const createdAt = Date.now()
    this.bookData = {
      bookId: this.bookId,
      lastEditedAt: createdAt,
      sortKey: createdAt,
      cover,
      pages: [],
      size: DEFAULT_BOOK_SIZE,
      quantity: 1,
      aiGeneratedCovers: [],
    }
    this.orderValidationStatus = PhotoBookOrderValidation.VALID
    // Enqueued immediately: the persist autorun is delayed, so a reload right
    // after creation would otherwise lose the book.
    return this.storagePersister.writeBook(this.bookData)
  }

  // Puts a book that was built elsewhere — the account copy, a restored cart, a
  // reorder — into this slot as it arrived: its identity and edit stamp are
  // what the parent's merge decided by, so they must survive the installation.
  @action
  replaceBook(book: StoredPhotoBook): Promise<boolean> {
    this.bookData = book
    this.orderValidationStatus = PhotoBookOrderValidation.VALID
    return this.storagePersister.writeBook(this.bookData)
  }

  // Every domain edit funnels through here: the caller hands an updater that
  // mutates the observable book in place, so the store stays agnostic of what
  // the edits mean. A session without a book has nothing to edit.
  @action
  updateBook(updater: PhotoBookUpdater): void {
    if (!this.bookData) return

    updater(this.bookData)
    this.bookData.lastEditedAt = Date.now()
  }

  @action
  setIsEditorInitializing(isInitializing: boolean): void {
    this.isEditorInitializing = isInitializing
  }

  @action
  setIsEditorMounted(isMounted: boolean): void {
    this.isEditorMounted = isMounted
  }

  @action
  setIsEditorReady(isReady: boolean): void {
    this.isEditorReady = isReady
  }

  @action
  clearOrderValidationWarning(): void {
    this.orderValidationStatus = PhotoBookOrderValidation.VALID
  }

  @action
  validateOrder(updateState = true): PhotoBookOrderValidation {
    const { photosCount } = this
    logger.info('validating book order', { photosCount })
    const status =
      photosCount < MIN_PHOTO_BOOK_PAGES
        ? PhotoBookOrderValidation.NOT_ENOUGH_PHOTOS
        : PhotoBookOrderValidation.VALID

    if (updateState) {
      this.orderValidationStatus = status
    }
    return status
  }

  // Drops the book from this store and its storage slot. A deletion is the
  // parent's to settle on both channels: the surviving books to the user cart,
  // this book's id to the order draft.
  @action
  clearBook(): void {
    this.bookData = null
    this.orderValidationStatus = PhotoBookOrderValidation.VALID
    this.storagePersister.clearStoredBook()
  }

  markBookAlreadyInDraft(book: StoredPhotoBook): void {
    this.draftSyncer?.markBookAlreadyInDraft(book)
  }

  resetDraftBaseline(): void {
    this.draftSyncer?.resetBaseline()
  }

  // Called when this store leaves its parent's map: an undisposed store's
  // autoruns would keep writing a deleted book's storage slot.
  dispose(): void {
    this.storagePersister.dispose()
    this.draftSyncer?.dispose()
  }

  // The book, or null while the editor is mid-init: before it has finished
  // rebuilding pages against the photo bank, a half-rebuilt book must not
  // reach storage or the remote channels.
  @computed
  get bookToPersist(): StoredPhotoBook | null {
    if (this.isEditorInitializing) {
      return null
    }

    return this.bookData
  }

  // On while this store holds a book that bookToPersist is withholding: the
  // book is here, the copy in memory just isn't fit to leave the editor yet.
  // A channel that publishes the books as one set needs this apart from an
  // empty store, where the same absence is the whole truth.
  @computed
  get isBookWithheldFromSync(): boolean {
    return this.bookData !== null && this.bookToPersist === null
  }
}
